import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LifeBuoy, Mail, MessageCircle, Phone } from 'lucide-react';
import { LogoMark } from '@/components/LogoMark';
import { SocialLinks } from '@/components/SocialLinks';
import { formatPhone } from '@/lib/format';
import { useLocalizedName } from '@/lib/useLocalizedName';
import { useContactLinks, useSite } from '@/lib/useSite';

/**
 * تذييل الصفحات العامّة (#54) — نفس تذييل الصفحة التعريفية، مستخرَجاً.
 *
 * `SiteFooter` هو تذييل **داخل** التطبيق: روابطه إلى `/support` و`/legal/*`
 * وهي خلف تسجيل الدخول، فمن فتح صفحة الشفافية من ملصق QR بلا حساب يضغط
 * «الشروط» فيجد نفسه في شاشة الدخول. هذا التذييل روابطه عامّة كلّها:
 * `/guide` · `/contact` · `/terms` · `/privacy`.
 *
 * `onLanding`: روابط أقسام الصفحة التعريفية (`#features`…) مراسٍ داخل
 * الصفحة نفسها، وخارجها تُصبح `/home#…` فتفتح الصفحة عند القسم.
 */
export function PublicFooter({ onLanding = false }: { onLanding?: boolean }) {
  const { t } = useTranslation();
  const localized = useLocalizedName();
  const { settings } = useSite();
  const contact = useContactLinks();
  const year = new Date().getFullYear();
  const appName = localized(settings.appNameEn, settings.appNameAr);
  const anchor = (hash: string) => (onLanding ? `#${hash}` : `/home#${hash}`);
  const legal = localized(settings.legalNoteEn, settings.legalNoteAr);

  return (
    <footer className="landing-footer">
      <div className="landing-footer__grid">
        <div>
          <span className="landing-brand">
            <LogoMark size={26} />
            <b>{appName}</b>
          </span>
          <p>{localized(settings.taglineEn, settings.taglineAr) || t('footer.tagline')}</p>
        </div>

        <nav>
          <span>{t('landing.nav.product')}</span>
          <a href={anchor('features')}>{t('landing.nav.features')}</a>
          <a href={anchor('how')}>{t('landing.nav.how')}</a>
          <a href={anchor('pricing')}>{t('landing.nav.pricing')}</a>
          <Link to="/login">{t('landing.signIn')}</Link>
        </nav>

        <nav>
          <span>{t('footer.help')}</span>
          <Link to="/guide">{t('footer.supportCentre')}</Link>
          <Link to="/contact">{t('footer.contactUs')}</Link>
          <Link to="/terms">{t('footer.terms')}</Link>
          <Link to="/privacy">{t('footer.privacy')}</Link>
        </nav>

        <nav>
          <span>{t('support.contactTitle')}</span>
          {contact.whatsapp && (
            <a href={contact.whatsapp}>
              <MessageCircle size={14} aria-hidden /> {formatPhone(settings.supportWhatsapp)}
            </a>
          )}
          {contact.email && (
            <a href={contact.email}>
              <Mail size={14} aria-hidden /> {settings.supportEmail}
            </a>
          )}
          {contact.phone && (
            <a href={contact.phone}>
              <Phone size={14} aria-hidden />{' '}
              {localized(settings.hoursEn, settings.hoursAr) || formatPhone(settings.supportPhone)}
            </a>
          )}
          <span className="landing-footer__address">
            <LifeBuoy size={14} aria-hidden />{' '}
            {localized(settings.addressEn, settings.addressAr)}
          </span>
          <SocialLinks links={contact.social} />
        </nav>
      </div>

      <p className="landing-footer__legal">
        {legal}
        {legal && ' · '}
        {t('footer.rights', { year, name: appName })}
      </p>
    </footer>
  );
}
